import { PLAN_FACTS, planName } from '@/shared/config/plans';
import { ROUTES } from '@/shared/config/routes';
import { formatCount, formatMonthDay } from '@/shared/lib/format';
import { summarizeUsage } from '@/shared/lib/pod-usage';
import type { WorkspaceData } from '@/shared/types/workspace';

export type OverviewBanner = {
  key: string;
  tone: 'warn' | 'bad' | 'info';
  role: 'status' | 'alert';
  title: string;
  text: string;
  action: string;
  href: string;
  isPrimary: boolean;
};

const isAgentConnected = (workspace: WorkspaceData): boolean => {
  const { claudeCode, codex } = workspace.connections;
  return [claudeCode.status, codex.status].some(
    (status) => status === 'CONNECTED' || status === 'KEY_REJECTED',
  );
};

/** The one banner the Overview shows, most urgent first (design: Main). */
export function selectOverviewBanner(workspace: WorkspaceData): OverviewBanner | null {
  const usage = summarizeUsage(workspace.usage);
  const subscription = workspace.billing.subscription;
  const name = planName(workspace.plan);

  if (subscription?.status === 'PAST_DUE') {
    const due = subscription.currentPeriodEndsAt ?? subscription.nextBilledAt;
    return {
      key: 'past-due',
      tone: 'bad',
      role: 'alert',
      title: 'Your last payment did not go through.',
      text: due
        ? `Update the card by ${formatMonthDay(due)}, or new sessions stop getting Pods.`
        : 'Update the card, or new sessions stop getting Pods.',
      action: 'Update payment method',
      href: ROUTES.BILLING,
      isPrimary: true,
    };
  }
  if (usage.isOut) {
    return {
      key: 'free-out',
      tone: 'bad',
      role: 'alert',
      title: 'Your free Pod-hours are used up.',
      text: 'New sessions will not get a Pod until you pick a plan.',
      action: 'Pick a plan',
      href: ROUTES.PLANS,
      isPrimary: true,
    };
  }
  if (subscription?.endsAt) {
    return {
      key: 'ends',
      tone: 'warn',
      role: 'status',
      title: `${name} ends on ${formatMonthDay(subscription.endsAt)}.`,
      text: 'After that, new sessions stop getting Pods. Keep the plan to carry on.',
      action: `Keep ${name}`,
      href: ROUTES.BILLING,
      isPrimary: false,
    };
  }
  if (usage.isNearlyOut) {
    return {
      key: 'free-80',
      tone: 'warn',
      role: 'status',
      title: `You have used ${usage.percentUsed} percent of your free Pod-hours.`,
      text: 'Pick a plan so new sessions keep getting Pods.',
      action: 'See plans',
      href: ROUTES.PLANS,
      isPrimary: true,
    };
  }
  if (!isAgentConnected(workspace)) {
    return {
      key: 'not-connected',
      tone: 'info',
      role: 'status',
      title: 'No agent is connected yet.',
      text: 'Sessions cannot reach Macswarm until you connect Claude Code or Codex.',
      action: 'Connect an agent',
      href: ROUTES.GET_STARTED,
      isPrimary: true,
    };
  }
  if (usage.isPastIncluded) {
    const extraCents = PLAN_FACTS[workspace.plan].extraPodHourCents ?? 0;
    const invoiceDate = subscription?.nextBilledAt ?? workspace.usage.resetsAt;
    return {
      key: 'past-included',
      tone: 'info',
      role: 'status',
      title: `You are past the ${formatCount(workspace.usage.includedPodHours)} included Pod-hours.`,
      text: `Extra hours cost $${(extraCents / 100).toFixed(2)} each and go on the ${
        invoiceDate ? `${formatMonthDay(invoiceDate)} ` : 'next '
      }invoice. Sessions keep running.`,
      action: 'See billing',
      href: ROUTES.BILLING,
      isPrimary: false,
    };
  }
  return null;
}
